import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import TierCard from "@/components/subscription/TierCard";
import UsageMeter from "@/components/subscription/UsageMeter";
import ChangeSubscriptionModal from "@/components/subscription/ChangeSubscriptionModal";
import PageBackButton from "@/components/PageBackButton";
import {
  getSubscriptionTiers,
  getMySubscription,
  getMyUsage,
  changeSubscription,
  cancelSubscription,
  startSubscriptionCheckout,
  confirmSubscriptionCheckout,
} from "@/api/subscriptions";
import { getGateways } from "@/api/payments";
import {
  buildCheckoutReturnUrl,
  checkoutErrorMessage,
  formatMoney,
  getRedirectGateways,
  readCheckoutReturn,
  withoutCheckoutParams,
} from "@/lib/billingCheckout";
import {
  Crown,
  RefreshCw,
  XCircle,
  BarChart3,
  Loader2,
  Clock,
  CalendarClock,
} from "lucide-react";
import { getTierIconComponent } from "@/components/subscription/tierIcons";

export default function SubscriptionPage() {
  // ── State ─────────────────────────────────────────────────────────
  const [tiers, setTiers] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [showChangeModal, setShowChangeModal] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [billingInterval, setBillingInterval] = useState("month");
  const [gateways, setGateways] = useState([]);
  const [confirmingCheckout, setConfirmingCheckout] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const handledReturnRef = useRef(null);

  // ── Data fetching ─────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [tiersData, subData, usageData, gatewayData] = await Promise.all([
        getSubscriptionTiers(),
        getMySubscription(),
        getMyUsage(),
        getGateways().catch(() => []),
      ]);
      setTiers(tiersData || []);
      setGateways(getRedirectGateways(gatewayData, { subscriptions: true }));
      setSubscription(subData?.status !== "none" ? subData : null);
      setUsage(
        (usageData?.features || []).filter(
          (feature) => feature?.limit !== null && feature?.limit !== undefined,
        ),
      );
    } catch (err) {
      toast.error("Failed to load subscription data");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // ── Return from the payment gateway ───────────────────────────────
  useEffect(() => {
    const checkoutReturn = readCheckoutReturn(searchParams);
    if (!checkoutReturn?.subscriptionId) return undefined;
    if (handledReturnRef.current === checkoutReturn.subscriptionId) return undefined;
    handledReturnRef.current = checkoutReturn.subscriptionId;
    setSearchParams(withoutCheckoutParams(searchParams), { replace: true });

    if (checkoutReturn.status === "cancelled") {
      toast.info("Checkout cancelled. Your plan hasn't changed.");
      return undefined;
    }

    let unmounted = false;
    const confirm = async () => {
      setConfirmingCheckout(true);
      try {
        // Activation can lag the approval by a few seconds at PayPal.
        for (let attempt = 0; attempt < 8 && !unmounted; attempt += 1) {
          const result = await confirmSubscriptionCheckout(checkoutReturn.subscriptionId);
          const sub = result?.subscription;
          if (sub?.status === "active" || sub?.status === "past_due") {
            toast.success(`You're now on the ${sub.tier_name} plan.`);
            break;
          }
          if (sub?.status === "scheduled") {
            const startDate = sub.starts_at
              ? new Date(sub.starts_at).toLocaleDateString()
              : "the end of your billing period";
            toast.success(`${sub.tier_name} starts on ${startDate}.`);
            break;
          }
          if (sub?.status === "abandoned" || sub?.status === "cancelled") {
            toast.error("The subscription wasn't approved in PayPal. Your plan hasn't changed.");
            break;
          }
          if (attempt === 7) {
            toast.info(
              "PayPal is still confirming your subscription. Your plan will update automatically within a few minutes.",
            );
          } else {
            await new Promise((resolve) => setTimeout(resolve, 2500));
          }
        }
      } catch (err) {
        toast.error(
          checkoutErrorMessage(err, "Couldn't confirm your subscription yet. Please refresh in a minute."),
        );
      } finally {
        if (!unmounted) {
          setConfirmingCheckout(false);
          try { window.dispatchEvent(new CustomEvent("valpha:subscription-changed")); } catch { /* ignore */ }
          fetchAll();
        }
      }
    };
    confirm();
    return () => {
      unmounted = true;
    };
  }, [searchParams, setSearchParams, fetchAll]);

  // ── Handlers ──────────────────────────────────────────────────────
  const handleTierSelect = (tier) => {
    setSelectedTier(tier);
    setShowChangeModal(true);
  };

  const handleConfirmChange = async (tier, _reason, selectedInterval = billingInterval) => {
    setActionLoading(true);
    try {
      if (!tier.is_free) {
        // Paid plans are approved at the gateway, which sends the user back here.
        const returnUrl = buildCheckoutReturnUrl("/subscription");
        const result = await startSubscriptionCheckout({
          tier_code: tier.code,
          interval: selectedInterval,
          gateway: gateways[0]?.name,
          success_url: returnUrl,
          cancel_url: returnUrl,
        });
        if (!result?.approval_url) {
          throw new Error("The payment provider didn't return a checkout link.");
        }
        window.location.assign(result.approval_url);
        return; // keep the loading state while the browser navigates away
      }

      await changeSubscription(tier.code);
      toast.success(`Successfully switched to ${tier.name}!`);
      setShowChangeModal(false);
      setSelectedTier(null);
      try { window.dispatchEvent(new CustomEvent("valpha:subscription-changed")); } catch { /* ignore */ }
      await fetchAll();
    } catch (err) {
      toast.error(checkoutErrorMessage(err, "Failed to change subscription"));
    }
    setActionLoading(false);
  };

  const handleCancel = async () => {
    setActionLoading(true);
    try {
      await cancelSubscription(cancelReason);
      toast.success("Subscription canceled. You'll keep access until the end of your billing period.");
      setShowCancelConfirm(false);
      setCancelReason("");
      await fetchAll();
    } catch (err) {
      toast.error(checkoutErrorMessage(err, "Failed to cancel subscription"));
    } finally {
      setActionLoading(false);
    }
  };

  // ── Helpers ───────────────────────────────────────────────────────
  const statusVariant = (s) => {
    const map = {
      active: "success",
      grace: "warning",
      canceled: "danger",
      past_due: "danger",
      expired: "danger",
    };
    return map[s] || "default";
  };

  const currentTierSort = tiers.find(
    (t) => t.code === subscription?.tier
  )?.sort_order;
  const currentTier = tiers.find((t) => t.code === subscription?.tier) ?? null;
  const CurrentTierIcon = getTierIconComponent(currentTier?.icon);
  const billing = subscription?.billing ?? null;
  const scheduledChange = subscription?.scheduled_change ?? null;
  const cancelsAtPeriodEnd = Boolean(subscription?.cancel_at_period_end);
  const formatLongDate = (value) =>
    new Date(value).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });

  // ── Render ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-8 pb-8">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="flex items-start gap-3">
          <PageBackButton label="Back to Settings" to="/settings" preferHistory={false} hideLabelOnMobile />
          <div>
          <h1 className="text-2xl font-bold text-textPrimary flex items-center gap-2">
            <Crown className="w-6 h-6 text-primary" />
            Subscription
          </h1>
          <p className="text-sm text-textSecondary mt-1">
            Manage your plan and feature access
          </p>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={fetchAll} title="Refresh">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* ─── Current Plan Banner ─────────────────────────────────── */}
      <Card>
        <CardContent className="p-5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <CurrentTierIcon className="w-6 h-6 text-primary" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-semibold text-textPrimary">
                    {subscription
                      ? subscription.tier_name ||
                        subscription.tier?.charAt(0).toUpperCase() +
                          subscription.tier?.slice(1)
                      : "No Active Plan"}
                  </h2>
                  {subscription && (
                    <Badge variant={statusVariant(subscription.status)}>
                      {subscription.status?.charAt(0).toUpperCase() +
                        subscription.status?.slice(1)}
                    </Badge>
                  )}
                </div>
                {billing && (
                  <p className="text-sm text-textSecondary mt-0.5">
                    {formatMoney(billing.amount, billing.currency)} / {billing.interval === "year" ? "year" : "month"}
                    {" "}via {billing.gateway === "paypal" ? "PayPal" : billing.gateway}
                  </p>
                )}
                {subscription?.current_period_end && (
                  <p className={`text-sm mt-0.5 ${cancelsAtPeriodEnd ? "text-yellow-400" : "text-textSecondary"}`}>
                    {cancelsAtPeriodEnd
                      ? scheduledChange
                        ? "Current plan ends "
                        : "Cancels on "
                      : billing
                        ? "Renews on "
                        : "Current period ends "}
                    {formatLongDate(subscription.current_period_end)}
                  </p>
                )}
                {subscription?.grace_end_at && subscription.status === "canceled" && (
                  <p className="text-sm text-yellow-400 mt-0.5">
                    Access until{" "}
                    {new Date(subscription.grace_end_at).toLocaleDateString(
                      undefined,
                      {
                        month: "long",
                        day: "numeric",
                        year: "numeric",
                      }
                    )}
                  </p>
                )}
                {!subscription && (
                  <p className="text-sm text-textSecondary mt-0.5">
                    Choose a plan below to get started
                  </p>
                )}
              </div>
            </div>
            {subscription &&
              ["active", "grace"].includes(subscription.status) &&
              !subscription.is_trial &&
              !cancelsAtPeriodEnd &&
              !currentTier?.is_free && (
              <Button
                variant="outline"
                size="sm"
                className="text-destructive border-destructive/30 hover:bg-destructive/10"
                onClick={() => setShowCancelConfirm(true)}
              >
                <XCircle className="w-4 h-4 mr-1.5" />
                Cancel Plan
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* ─── Checkout confirmation / scheduled change ─────────────── */}
      {confirmingCheckout && (
        <Card className="border-primary/40 bg-primary/5">
          <CardContent className="p-4 flex items-center gap-3 text-sm text-textPrimary">
            <Loader2 className="w-4 h-4 animate-spin text-primary" />
            Confirming your subscription with PayPal...
          </CardContent>
        </Card>
      )}
      {scheduledChange && (
        <Card className="border-primary/30">
          <CardContent className="p-4 flex items-start gap-3">
            <CalendarClock className="w-5 h-5 text-primary mt-0.5" />
            <div className="text-sm">
              <div className="font-semibold text-textPrimary">
                Switching to {scheduledChange.tier_name} (
                {scheduledChange.interval === "year" ? "yearly" : "monthly"}) on{" "}
                {scheduledChange.starts_at
                  ? formatLongDate(scheduledChange.starts_at)
                  : "your next billing date"}
              </div>
              <div className="text-xs text-textSecondary mt-0.5">
                {formatMoney(scheduledChange.amount, scheduledChange.currency)} will be charged when it starts.
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ─── Trial status callout ────────────────────────────────── */}
      {subscription?.is_trial && (() => {
        const ends = subscription.trial_ends_at || subscription.current_period_end;
        const daysLeft = ends
          ? Math.max(0, Math.ceil((new Date(ends).getTime() - Date.now()) / 86_400_000))
          : null;
        const inGrace = subscription.status === "grace";
        const trialName = subscription.tier_name || "trial";
        return (
          <Card className="border-[#4FA9A1]/40 bg-[#4FA9A1]/5">
            <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-[#4FA9A1] mt-0.5" />
                <div>
                  <div className="text-sm font-semibold text-textPrimary">
                    {inGrace
                      ? "Trial ended — you're in grace period"
                      : daysLeft === 0
                      ? "Your trial ends today"
                      : `Your free ${trialName} trial ends in ${daysLeft} day${daysLeft === 1 ? "" : "s"}`}
                  </div>
                  <div className="text-xs text-textSecondary mt-0.5">
                    Pick a paid plan below to keep access without interruption.
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* ─── Plan Comparison Grid ────────────────────────────────── */}
      <div>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="text-lg font-semibold text-textPrimary">
            Available Plans
          </h2>
          {gateways[0]?.environment === "sandbox" && (
            <Badge variant="warning">PayPal sandbox: test payments only</Badge>
          )}
        </div>
        {tiers.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <p className="text-textSecondary">
                No subscription plans are currently available. Please check back
                later.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {tiers.map((tier) => (
              <TierCard
                key={tier.code}
                tier={tier}
                currentTierCode={subscription?.tier}
                currentTierSort={currentTierSort}
                onSelect={handleTierSelect}
                loading={actionLoading}
              />
            ))}
          </div>
        )}
      </div>

      {/* ─── Feature Usage Meters ────────────────────────────────── */}
      {usage.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-textPrimary mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-primary" />
            Feature Usage
          </h2>
          <Card>
            <CardContent className="p-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {usage.map((feat) => (
                  <UsageMeter
                    key={feat.feature}
                    label={feat.feature
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (c) => c.toUpperCase())}
                    used={feat.remaining}
                    limit={feat.limit}
                    window={feat.window}
                    windowEnd={feat.window_end}
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ─── Change Subscription Modal ───────────────────────────── */}
      <ChangeSubscriptionModal
        open={showChangeModal}
        onClose={() => {
          setShowChangeModal(false);
          setSelectedTier(null);
        }}
        selectedTier={selectedTier}
        currentTier={
          subscription
            ? tiers.find((t) => t.code === subscription.tier)
            : null
        }
        subscription={subscription}
        interval={billingInterval}
        onIntervalChange={setBillingInterval}
        gateway={gateways[0] ?? null}
        onConfirm={handleConfirmChange}
        loading={actionLoading}
      />

      {/* ─── Cancel Confirmation Modal ───────────────────────────── */}
      {showCancelConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => !actionLoading && setShowCancelConfirm(false)}
          />
          <div className="relative z-10 w-full max-w-md rounded-xl border border-borderDefault bg-backgroundSurface shadow-xl p-6 space-y-4">
            <h3 className="text-lg font-semibold text-textPrimary">
              Cancel Subscription
            </h3>
            <p className="text-sm text-textSecondary">
              {billing ? "You won't be charged again. " : ""}
              {subscription?.current_period_end
                ? `Your plan stays active until ${formatLongDate(subscription.current_period_end)}, then you'll move to the Free plan.`
                : "You'll move to the Free plan right away."}
            </p>
            <div>
              <label className="block text-sm text-textSecondary mb-1.5">
                Reason (optional)
              </label>
              <textarea
                className="w-full rounded-md border border-borderDefault bg-backgroundSurface text-textPrimary p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                rows={3}
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Let us know why you're canceling..."
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCancelConfirm(false)}
                disabled={actionLoading}
              >
                Keep Plan
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleCancel}
                disabled={actionLoading}
              >
                {actionLoading ? "Canceling..." : "Confirm Cancellation"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
